
import { Component, Prop, Vue } from 'vue-property-decorator';
import { IInsightData, InsightMetric, ResponseType } from '@/models/insight';

@Component({})
export default class InsightMetrics extends Vue {
  @Prop({ required: true })
  public insight!: IInsightData;

  @Prop({ default: 'responsive' })
  public layout!: 'single' | 'horizontal' | 'vertical' | 'grid' | 'responsive';

  @Prop({ default: false })
  public showDebug!: boolean;

  @Prop({ default: 4 })
  public maxMetrics!: number;

  // =============================================================================
  // EXPLICIT RESPONSE TYPE METRICS SUPPORT  
  // =============================================================================

  get hasValidMetrics(): boolean {
    return !!(this.insight.data?.metrics?.length || this.insight.value !== undefined);
  }

  get displayMetrics(): InsightMetric[] {
    // If structured metrics exist, use them
    if (this.insight.data?.metrics?.length) {
      return this.insight.data.metrics.slice(0, this.maxMetrics);
    }

    // If single value exists, create a metric from it
    if (this.insight.value !== undefined) {
      return [{
        name: this.insight.category,
        value: this.insight.value,
        description: this.insight.insight,
        trend: this.insight.trend,
        changePercentage: this.insight.change,
        unit: undefined
      }];
    }

    return [];
  }

  get primaryMetric(): InsightMetric {
    return this.displayMetrics[0] || {
      name: 'No Data',
      value: 0,
      description: 'No metric data available'
    };
  }

  public getMetricTypeColor(): string {
    const metricResponseTypes: ResponseType[] = ['single_number', 'key_metrics'];
    
    if (!this.insight.response_type) return 'grey';
    if (this.insight.type_mismatch) return 'error';
    if (metricResponseTypes.includes(this.insight.response_type)) return 'success';
    return 'warning';
  }

  public getMetricTypeIcon(): string {
    if (!this.insight.response_type) return 'mdi-help-circle';
    if (this.insight.type_mismatch) return 'mdi-alert-circle';
    
    const iconMap: Record<ResponseType, string> = {
      'single_number': 'mdi-numeric',
      'key_metrics': 'mdi-view-dashboard',
      'data_table': 'mdi-table',
      'comparison': 'mdi-compare',

      'trend_data': 'mdi-trending-up',
      'summary_text': 'mdi-text',
      'auto': 'mdi-auto-fix',
      'list': 'mdi-format-list-bulleted'
    };
    
    return iconMap[this.insight.response_type] || 'mdi-chart-bar';
  }

  public getLayoutColor(): string {
    return this.layout === 'responsive' ? 'info' : 'primary';
  }

  // =============================================================================
  // LAYOUT HELPERS
  // =============================================================================

  public getMetricsClass(): string {
    const classes = [`insight-metrics--${this.layout}`];
    
    if (this.insight.type_mismatch) {
      classes.push('insight-metrics--mismatch');
    }
    
    return classes.join(' ');
  }

  public getGridCols(): number {
    if (this.displayMetrics.length === 1) return 12;
    if (this.displayMetrics.length === 2) return 6;
    if (this.displayMetrics.length <= 4) return 6;
    return 4;
  }

  public getGridMd(): number {
    if (this.displayMetrics.length === 1) return 12;
    if (this.displayMetrics.length === 2) return 6;
    return 4;
  }

  // =============================================================================
  // FORMATTING AND STYLING HELPERS
  // =============================================================================

  public formatMetricValue(value: number): string {
    if (typeof value !== 'number') return '0';
    
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    } else if (value >= 100) {
      return value.toFixed(0);
    } else if (value >= 1) {
      return value.toFixed(1);
    } else {
      return value.toFixed(2);
    }
  }

  public getTrendIcon(trend: string): string {
    const iconMap: Record<string, string> = {
      up: 'mdi-trending-up',
      down: 'mdi-trending-down',
      stable: 'mdi-trending-neutral'
    };
    return iconMap[trend] || 'mdi-minus';
  }

  public getTrendColor(trend: string): string {
    const colorMap: Record<string, string> = {
      up: 'success',
      down: 'error',
      stable: 'warning'
    };
    return colorMap[trend] || 'grey';
  }

  public getChangeClass(changePercentage: number): string {
    if (changePercentage > 0) return 'success--text';
    if (changePercentage < 0) return 'error--text';
    return 'text--secondary';
  }
}
